import { BookOpen, Download, Wifi, Loader2 } from "lucide-react";
import { useState } from "react";
import { downloadOfflineFile } from "@/lib/api";
import { toast } from "sonner";

// Cipher reference content — each entry: how it works + short worked example
const CIPHERS_INFO = [
  {
    key: "english_ordinal", name: "English Ordinal", tier: "Big 4",
    rule: "Each letter is replaced with its position in the alphabet. A=1, B=2, C=3 … Z=26.",
    example: "JESUS → J=10, E=5, S=19, U=21, S=19 → 10+5+19+21+19 = 74",
    notes: "The foundation of modern English gematria. Hubbard's #1 cipher.",
  },
  {
    key: "full_reduction", name: "Full Reduction", tier: "Big 4",
    rule: "Each letter is reduced to a single digit (1–9) using digit-sum. A=1, B=2 … I=9, J=10→1, K=11→2 … S=19→10→1, Z=26→8.",
    example: "JESUS → J=1, E=5, S=1, U=3, S=1 → 1+5+1+3+1 = 11",
    notes: "Also called Pythagorean. Removes the large numbers from Ordinal, exposing the underlying small-number structure.",
  },
  {
    key: "reverse_ordinal", name: "Reverse Ordinal", tier: "Big 4",
    rule: "Ordinal in reverse. Z=1, Y=2, X=3 … A=26.",
    example: "JESUS → J=17, E=22, S=8, U=6, S=8 → 17+22+8+6+8 = 61",
    notes: "Mirrors English Ordinal. Reveals decode pairs that work in both directions.",
  },
  {
    key: "reverse_reduction", name: "Reverse Reduction", tier: "Big 4",
    rule: "Reverse Ordinal reduced to single digits. Z=1, Y=2 … H=19→10→1, A=26→8.",
    example: "JESUS → J=8, E=4, S=8, U=6, S=8 → 8+4+8+6+8 = 34",
    notes: "Hubbard's death-cipher signature lives here. Try DEATH = 25, KILL = 28.",
  },
  {
    key: "english_extended", name: "English Extended", tier: "Headline",
    rule: 'Hebrew-influenced "Standard" cipher. A–I = 1–9 then values jump: J=600, K=10, L=20, M=30, N=40, O=50, P=60, Q=70, R=80, S=90, T=100, U=200, V=700, W=900, X=300, Y=400, Z=500.',
    example: "CORONAVIRUS → 3+50+80+50+40+1+700+9+80+200+90 = 1303",
    notes: "Produces the big 4-digit headline numbers (CORONAVIRUS=1303, DONALD TRUMP=589). Gematrinator calls this 'Standard'.",
  },
  {
    key: "english_sumerian", name: "English Sumerian",
    rule: "English Ordinal multiplied by 6.",
    example: "JESUS → Ordinal 74 × 6 = 444",
    notes: "Designed to expose triple-digit alignments. JESUS=444 is its most famous decode.",
  },
  {
    key: "reverse_sumerian", name: "Reverse Sumerian",
    rule: "Reverse Ordinal multiplied by 6.",
    example: "JESUS → Reverse Ordinal 61 × 6 = 366",
    notes: "Mirror partner of Sumerian.",
  },
  {
    key: "jewish", name: "Jewish / Latin",
    rule: "Hebrew-style letter values mapped to Latin alphabet — small values for A–I (1–9), larger jumps after.",
    example: "JESUS → 621",
    notes: "Used by some decoders as a cross-check against English Extended. Distinct table from Extended in our engine.",
  },
  {
    key: "satanic", name: "Satanic",
    rule: "Each letter offset by +35 from Ordinal. A=36, B=37 … Z=61.",
    example: "JESUS → 45+40+54+56+54 = 249",
    notes: "Created by Anton LaVey's circle. Produces inflated values that decoders compare against ritual numbers.",
  },
  {
    key: "reverse_satanic", name: "Reverse Satanic",
    rule: "Reverse Ordinal + 35. Z=36, Y=37 … A=61.",
    example: "JESUS → 52+57+43+41+43 = 236",
    notes: "Mirror of Satanic.",
  },
  {
    key: "primes", name: "Primes",
    rule: "Each letter mapped to the nth prime number. A=2, B=3, C=5, D=7, E=11, F=13, G=17 … Z=101.",
    example: "JESUS → J=29, E=11, S=67, U=73, S=67 → 247",
    notes: "Reveals prime-number relationships. 317 (Lord Jacob Rothschild) is the 66th prime — that kind of trick.",
  },
  {
    key: "reverse_primes", name: "Reverse Primes",
    rule: "Primes mapped in reverse. Z=2, Y=3 … A=101.",
    example: "JESUS → J=59, E=79, S=19, U=13, S=19 → 189",
    notes: "Mirror of Primes.",
  },
  {
    key: "chaldean", name: "Chaldean",
    rule: "Ancient Babylonian numerology. Letters mapped 1–8 (the number 9 is sacred and excluded). A=1, B=2, C=3, D=4, E=5, F=8, G=3, H=5, I=1, J=1, K=2 …",
    example: "JESUS → 1+5+3+6+3 = 18",
    notes: "Pre-dates Pythagorean reduction by ~2,500 years. Niche but cited by esoteric decoders.",
  },
  {
    key: "alw_kabbalah", name: "ALW Kabbalah",
    rule: 'English Qabalah variant. A=1, L=2, W=3, H=4, S=5, D=6, O=7, Z=8, K=9, V=10, G=11, R=12, C=13, N=14, Y=15, J=16, U=17, F=18, Q=19, B=20, M=21, X=22, I=23, T=24, E=25, P=26.',
    example: "JESUS → 16+25+5+17+5 = 68",
    notes: "Acronym from the cipher's first three letters: A-L-W. Used in Thelemic / Crowley-aligned decoding.",
  },
  {
    key: "kfw_kabbalah", name: "KFW Kabbalah",
    rule: "Another English Qabalah variant. K=1, F=2, W=3 … and so on. Different letter-to-number mapping than ALW.",
    example: "JESUS → produces its own distinct value",
    notes: "Used as a cross-cipher comparator. Often referenced alongside ALW and LCH.",
  },
  {
    key: "lch_kabbalah", name: "LCH Kabbalah",
    rule: "Third English Qabalah variant. L=1, C=2, H=3 … its own ordering.",
    example: "JESUS → produces its own distinct value",
    notes: "Round out the Kabbalah trio. Decoders compare ALW / KFW / LCH for resonance.",
  },
  {
    key: "trigonal", name: "Trigonal",
    rule: "Triangular numbers. Each letter = T(n) = n × (n+1) / 2 where n is the Ordinal position. A=1, B=3, C=6, D=10, E=15, F=21, G=28 … Z=351.",
    example: "JESUS → 55+15+190+231+190 = 681",
    notes: "Geometric / sacred-geometry framing. Triangular numbers connect to the Pythagorean Tetractys.",
  },
  {
    key: "squares", name: "Squares",
    rule: "Each letter = its Ordinal position squared. A=1, B=4, C=9, D=16 … Z=676.",
    example: "JESUS → 100+25+361+441+361 = 1288",
    notes: "Square-number framing. 666 = 6² × 18.5 (not clean), but Squares-cipher decodes find different alignments.",
  },
  {
    key: "reverse_squares", name: "Reverse Squares",
    rule: "Reverse Ordinal squared. Z=1, Y=4 … A=676.",
    example: "JESUS → 289+484+64+36+64 = 937",
    notes: "Mirror partner of Squares.",
  },
  {
    key: "septenary", name: "Septenary",
    rule: "7-cycle wave pattern. A=1, B=2 … G=7 (climb), H=6, I=5 … M=1 (descend), N=2 … S=7 (climb), T=6 … Y=1 (descend), Z=2 (climb begins again).",
    example: "JESUS → 4+5+7+5+7 = 28",
    notes: "Compact 1–7 mapping. Named after septem (Latin for 7). Smaller value range — useful for spotting low-number alignments.",
  },
];

const DIGIT_NOTE = {
  title: "Digits in your input",
  body: 'When a phrase contains digit-runs ("23", "666", "9 11"), every cipher adds those integer values to the letter-sum. So "The Number 23" → letters sum 47 (Reverse Reduction) + integer 23 = 70. Matches gematrinator.com exactly.',
};

export default function CiphersGuide() {
  const [downloading, setDownloading] = useState(false);

  const downloadOffline = async () => {
    setDownloading(true);
    try {
      await downloadOfflineFile();
      toast.success("Gem Key Offline downloaded — open it in any browser anywhere.");
    } catch {
      toast.error("Download failed — please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="space-y-10 max-w-5xl mx-auto">
      <header className="space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted">// Ciphers</div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight">
          How each <span className="gold italic">cipher</span> works
        </h1>
        <p className="muted text-sm max-w-3xl leading-relaxed">
          Gem Key uses all 20 ciphers that appear on gematrinator.com. Every cipher is a deterministic
          letter-to-number map — same input always produces the same number. Below is the rule, an example,
          and how decoders use each one.
        </p>
      </header>

      {/* Offline Edition download — kept deliberately simple to render robustly on every device */}
      <div
        className="panel p-4 md:p-6 border-[rgba(212,175,55,0.4)]"
        data-testid="offline-download-panel"
        style={{ maxWidth: "100%", overflowWrap: "break-word" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Wifi className="gold shrink-0" size={16} />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] gold">
            Gem Key Offline Edition
          </span>
        </div>
        <div className="text-base md:text-xl font-semibold mb-2 leading-snug">
          Take the whole dictionary <span className="gold">offline</span>.
        </div>
        <p className="muted text-sm leading-relaxed mb-4">
          One HTML file (~530&nbsp;KB) with every phrase, all 20 ciphers, the search,
          calculator and date tools. Save it to your phone or USB stick — open it in any
          browser to decode without internet.
        </p>
        <button
          onClick={downloadOffline}
          disabled={downloading}
          data-testid="download-offline-btn"
          className="btn-primary px-5 py-3 text-xs flex items-center justify-center gap-2 disabled:opacity-50 w-full md:w-auto"
        >
          {downloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
          {downloading ? "Building…" : "Download Offline"}
        </button>
      </div>

      {/* Digit handling callout */}
      <div className="panel panel-narrative p-5 flex gap-4 items-start" data-testid="digit-note">
        <BookOpen className="gold shrink-0 mt-1" size={20} />
        <div>
          <div className="font-display text-lg mb-1">{DIGIT_NOTE.title}</div>
          <div className="text-sm muted font-mono leading-relaxed">{DIGIT_NOTE.body}</div>
        </div>
      </div>

      {/* Cipher cards */}
      <div className="space-y-4">
        {CIPHERS_INFO.map((c, i) => (
          <article
            key={c.key}
            className="panel panel-hover p-6"
            data-testid={`cipher-card-${c.key}`}
          >
            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[10px] muted">#{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl tracking-tight gold">{c.name}</h2>
              </div>
              {c.tier && (
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] gold">
                  // {c.tier}
                </span>
              )}
            </div>
            <div className="space-y-3 text-sm leading-relaxed">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] muted mb-1">Rule</div>
                <div>{c.rule}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] muted mb-1">Example</div>
                <div className="font-mono text-[13px] text-[var(--primary)] bg-[rgba(212,175,55,0.06)] px-3 py-2 border-l-2 border-[var(--primary)]">
                  {c.example}
                </div>
              </div>
              {c.notes && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] muted mb-1">How decoders use it</div>
                  <div className="muted">{c.notes}</div>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      <footer className="muted text-xs font-mono text-center pt-6 border-t border-white/10">
        20 ciphers · math-deterministic · gematrinator.com parity verified
      </footer>
    </section>
  );
}
